import React, { useState } from "react";
import { displaySpeaker } from "./transcriptDocument";

/** A voice already in the transcript, with how many lines it has. */
export interface TranscriptVoice {
  label: string;
  lines: number;
}

export interface SpeakerMovePickerProps {
  /** The transcript's speakers other than the current line's. */
  speakers: TranscriptVoice[];
  /** The label "New speaker" would take, when the server has said. */
  newSpeaker?: string;
  /** Move the line(s) to `speaker`, or to a new placeholder for null. */
  onMove: (speaker: string | null) => Promise<void>;
  /** Why the last move was refused. */
  error: string | null;
}

const row =
  "flex cursor-pointer flex-col rounded-md px-2 py-1.5 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none aria-disabled:cursor-default aria-disabled:opacity-60";

/**
 * Where a transcript line moves (#13917): another of the transcript's
 * voices, or a new one. It fixes which voice said the line, never who a
 * voice is, so it offers no people and takes no name; naming is the
 * speaker panel's section below it.
 */
const SpeakerMovePicker: React.FC<SpeakerMovePickerProps> = ({
  speakers,
  newSpeaker,
  onMove,
  error,
}) => {
  const [saving, setSaving] = useState(false);

  const move = async (speaker: string | null) => {
    if (saving) return;
    setSaving(true);
    try {
      await onMove(speaker);
    } finally {
      setSaving(false);
    }
  };

  // The rows take the keyboard too: there is no field to type into.
  const activate = (e: React.KeyboardEvent, speaker: string | null) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    void move(speaker);
  };

  return (
    <div>
      <p className="mb-1 px-2 font-mono text-[11px] text-[#6E7480]">Move to</p>
      <ul role="listbox" aria-label="Move to">
        {speakers.map((speaker) => (
          <li
            key={speaker.label}
            role="option"
            aria-selected={false}
            aria-disabled={saving}
            tabIndex={0}
            onClick={() => void move(speaker.label)}
            onKeyDown={(e) => activate(e, speaker.label)}
            className={row}
          >
            <span className="truncate text-sm text-[#10141B]">
              {displaySpeaker(speaker.label)}
            </span>
            <span className="font-mono text-[11px] text-[#6E7480]">
              {speaker.lines === 1 ? "1 line" : `${speaker.lines} lines`}
            </span>
          </li>
        ))}
        <li
          role="option"
          aria-selected={false}
          aria-disabled={saving}
          tabIndex={0}
          onClick={() => void move(null)}
          onKeyDown={(e) => activate(e, null)}
          className={`${row} ${speakers.length > 0 ? "mt-1 border-t border-gray-100 pt-2" : ""}`}
        >
          <span className="text-sm text-[#10141B]">New speaker</span>
          {newSpeaker && (
            <span className="font-mono text-[11px] text-[#6E7480]">
              {displaySpeaker(newSpeaker)}
            </span>
          )}
        </li>
      </ul>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default SpeakerMovePicker;
